import { Forms } from "@/types/form";
import {
    EmailAuthProvider,
    GoogleAuthProvider,
    User,
    linkWithCredential,
    linkWithRedirect,
    reauthenticateWithCredential,
    unlink,
    updatePassword,
} from "firebase/auth";
import { log } from "@/utils/logger";
import { auth } from "@/config/firebase";
import { useUser } from "@/context/user";
import { useState } from "react";
import { getRoutes, handleRouting } from "@/utils/helpers";

const useSubmit = () => {
    const update = useUser(state => state.update);
    const routes = getRoutes();

    const onSubmit = async (data: Forms["Profile"]) => {
        try {
            const { currentPassword, newPassword, password, email } = data;

            if (email && currentPassword && newPassword) {
                await handlePasswordChange(email, currentPassword, newPassword);
            }
            if (email && password) {
                await handleAccountLinking(email, password);
            }

            await update();
            log.success("Profile has been successfully updated.");
            handleRouting(routes.organization);
        } catch (e) {
            log.error(e, "onSubmit");
        }
    };

    return onSubmit;
};

const handleAccountLinking = async (email: string, password: string) => {
    const user = auth.currentUser as User;
    const credential = EmailAuthProvider.credential(email, password);
    await linkWithCredential(user, credential);
};

const handlePasswordChange = async (
    email: string,
    currentPassword: string,
    newPassword: string,
) => {
    const user = auth.currentUser as User;
    const credential = EmailAuthProvider.credential(email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
};

const useUnlinkGoogle = () => {
    const [loading, setLoading] = useState(false);
    const update = useUser(state => state.update);

    const onUnlink = async () => {
        try {
            setLoading(true);

            const user = auth.currentUser as User;
            await unlink(user, "google.com");
            await update();
        } catch (e) {
            log.error(e, "useUnlinkGoogle.onUnlink");
        } finally {
            setLoading(false);
        }
    };

    return {
        unLinking: loading,
        onUnlink,
    };
};

const useLinkGoogle = () => {
    const [loading, setLoading] = useState(false);

    const onLink = async () => {
        try {
            setLoading(true);
            const user = auth.currentUser as User;

            const provider = new GoogleAuthProvider();

            await linkWithRedirect(user, provider);
        } catch (e) {
            log.error(e, "useLinkGoogle.onLink");
        } finally {
            setLoading(false);
        }
    };

    return {
        onLink,
        linking: loading,
    };
};

export { useSubmit, useUnlinkGoogle, useLinkGoogle };
